import { useState } from "react";
import { apiClient } from "@/api/client";
import { formatApiError } from "@/utils/httpErrors";
import { TIER_FINDING_COUNTS } from "@/lib/qoeCatalogue.generated";
import { complexityForVertical, complexityLabel, INDUSTRY_QUESTION_ID } from "@/lib/industryComplexity";

interface ScopeSelectorProps {
  dealId: string;
  onComplete: (tier: "lite" | "focused" | "full", score: number) => void;
  onCancel?: () => void;
  /** QOE-CPA-FIXPACK-1 D5 — the engagement's industry, captured once on the New
   *  Engagement form. Prefills the industry question below rather than asking
   *  for the same fact a second time. Optional: an older deal without one, or
   *  an industry with no defensible complexity weight, just leaves it blank. */
  vertical?: string | null;
}

interface Question {
  id: string;
  text: string;
  options: { label: string; score: number }[];
}

const QUESTIONS: Question[] = [
  {
    id: "entity_count",
    text: "How many legal entities are involved in this transaction?",
    options: [
      { label: "Single entity", score: 1 },
      { label: "2–3 entities", score: 2 },
      { label: "4+ entities", score: 3 },
    ],
  },
  {
    id: "deal_size",
    text: "What is the approximate deal size (enterprise value)?",
    options: [
      { label: "Under $5M", score: 1 },
      { label: "$5M – $25M", score: 2 },
      { label: "Over $25M", score: 3 },
    ],
  },
  {
    id: "related_party",
    text: "Are there related-party transactions, owner-comp, or personal expenses in the books?",
    options: [
      { label: "None identified", score: 1 },
      { label: "Some — under 10% of revenue", score: 2 },
      { label: "Significant — over 10% of revenue", score: 3 },
    ],
  },
  {
    id: "industry",
    text: "What is the primary industry?",
    options: [
      { label: "Service / Professional Services", score: 1 },
      { label: "Construction / HVAC / Field Services", score: 2 },
      { label: "Manufacturing / O&G / Healthcare / Multi-vertical", score: 3 },
    ],
  },
  {
    id: "buyer_type",
    text: "Who is the buyer?",
    options: [
      { label: "Individual / Search Fund", score: 1 },
      { label: "Strategic acquirer", score: 2 },
      { label: "Private Equity / Institutional", score: 3 },
    ],
  },
];

function scoreToTier(score: number): "lite" | "focused" | "full" {
  if (score <= 7) return "lite";
  if (score <= 11) return "focused";
  return "full";
}

// The reachable score range, derived from QUESTIONS so it stays honest if a
// question is added or re-weighted. Every question is mandatory and the
// cheapest answer still scores 1, so the floor is 5, not 0 — printing "8 / 15"
// implies a 0-based scale that does not exist and makes an ordinary deal read
// as low-complexity. scope_score_scale() in qoe_context.py states the same
// range on the rendered report; these two must agree.
const SCORE_MIN = QUESTIONS.reduce((s, q) => s + Math.min(...q.options.map((o) => o.score)), 0);
const SCORE_MAX = QUESTIONS.reduce((s, q) => s + Math.max(...q.options.map((o) => o.score)), 0);

// MIR point counts are derived from the catalogue (17/54/74) — writing them
// here is what let the scope screen advertise stale counts after the catalogue moved.
const TIER_LABELS: Record<string, { label: string; pts: string; desc: string }> = {
  lite:    { label: "QoE Lite",    pts: `${TIER_FINDING_COUNTS.lite} MIR points`,    desc: "Streamlined review for smaller, lower-complexity deals." },
  focused: { label: "Focused QoE", pts: `${TIER_FINDING_COUNTS.focused} MIR points`, desc: "Balanced depth for mid-market transactions." },
  full:    { label: "Full QoE",    pts: `${TIER_FINDING_COUNTS.full} MIR points`,    desc: "Comprehensive analysis for complex or institutional deals." },
};

export function ScopeSelector({ dealId, onComplete, onCancel, vertical }: ScopeSelectorProps) {
  const [step, setStep] = useState(0);
  // D5: seed the industry weight from the engagement's vertical. Lazy initial
  // state, not an effect — the prefill is a starting value the CPA can change,
  // and an effect would fight the user by re-applying it on every render.
  const prefilledIndustry = complexityForVertical(vertical);
  const [answers, setAnswers] = useState<Record<string, number>>(
    prefilledIndustry === null ? {} : { [INDUSTRY_QUESTION_ID]: prefilledIndustry },
  );
  // QOE-TRUTH-AND-JOURNEY-1 R6 — a resolved prefill is a fact, not a question,
  // until the CPA explicitly asks to change it. Once asked, stays a question
  // for the rest of this wizard instance — flipping back would just re-hide
  // the override the CPA already reached for.
  const [industryOverridden, setIndustryOverridden] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const question = QUESTIONS[step];
  const answered = question.id in answers;
  const totalQuestions = QUESTIONS.length;

  const totalScore = Object.values(answers).reduce((s, v) => s + v, 0);
  const tier = scoreToTier(totalScore);
  const tierInfo = TIER_LABELS[tier];

  function selectAnswer(score: number) {
    setAnswers((prev) => ({ ...prev, [question.id]: score }));
  }

  function next() {
    if (step < totalQuestions - 1) {
      setStep((s) => s + 1);
    } else {
      setConfirming(true);
    }
  }

  function back() {
    if (confirming) {
      setConfirming(false);
    } else {
      setStep((s) => Math.max(0, s - 1));
    }
  }

  async function confirm() {
    setSaving(true);
    setError(null);
    try {
      await apiClient.patch(`/qoe/deals/${dealId}/scope`, {
        scope_tier: tier,
        score: totalScore,
        override: false,
      });
      onComplete(tier, totalScore);
    } catch (e: unknown) {
      setError(formatApiError(e));
      setSaving(false);
    }
  }

  if (confirming) {
    return (
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">Confirm Engagement Scope</h3>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-1">
          <p className="text-lg font-bold text-gray-900">{tierInfo.label}</p>
          <p className="text-sm text-gray-600">{tierInfo.pts}</p>
          <p className="text-sm text-gray-500 mt-1">{tierInfo.desc}</p>
          <p className="text-xs text-gray-400 mt-2">
            Complexity score: {totalScore} (range {SCORE_MIN}–{SCORE_MAX})
          </p>
        </div>
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={back}
            disabled={saving}
            className="px-4 py-2 text-sm border rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Confirm Scope"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>Step {step + 1} of {totalQuestions}</span>
        <span>{Math.round(((step) / totalQuestions) * 100)}% complete</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div
          className="bg-blue-500 h-1.5 rounded-full transition-all"
          style={{ width: `${((step) / totalQuestions) * 100}%` }}
        />
      </div>

      <h3 className="font-semibold text-gray-900 text-base leading-snug">
        {question.text}
      </h3>

      {question.id === INDUSTRY_QUESTION_ID && prefilledIndustry !== null && !industryOverridden ? (
        // R6 — a resolved fact, not a question the industry doesn't appear in.
        // The vertical answered this already; render what it resolved to, not
        // three buckets the CPA has to re-map "Dental Practice" onto.
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-gray-900">
              <span className="font-medium">{vertical}</span> → Complexity: {complexityLabel(prefilledIndustry)}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Prefilled from this engagement's industry.</p>
          </div>
          <button
            type="button"
            onClick={() => setIndustryOverridden(true)}
            className="shrink-0 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {question.id === INDUSTRY_QUESTION_ID && prefilledIndustry !== null && (
            <p className="text-xs text-gray-500">
              Prefilled from this engagement's industry ({vertical}). Change it if
              this deal is more or less complex than the industry norm.
            </p>
          )}
          {question.options.map((opt) => {
            const selected = answers[question.id] === opt.score;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => selectAnswer(opt.score)}
                className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors
                  ${selected
                    ? "border-blue-500 bg-blue-50 text-blue-900 font-medium"
                    : "border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700"
                  }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <div className="flex gap-2">
          {step > 0 && (
            <button
              type="button"
              onClick={back}
              className="px-4 py-2 text-sm border rounded text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
          )}
          {onCancel && step === 0 && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={next}
          disabled={!answered}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {step === totalQuestions - 1 ? "Review" : "Next"}
        </button>
      </div>
    </div>
  );
}
